/**
 * PM Available Projects Component
 * Shows projects available for PM to apply
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Chip,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Divider
} from '@mui/material';
import {
    LocationOn,
    AttachMoney,
    Person,
    Send,
    Business
} from '@mui/icons-material';
import projectManagerApi from '../../services/projectManagerApi';
import DialogCloseButton from '../common/DialogCloseButton';

const PMAvailableProjects = ({ onApplicationSubmitted }) => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [applyDialogOpen, setApplyDialogOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [applicationData, setApplicationData] = useState({
        proposalMessage: '',
        proposedFeePercentage: 10.0,
        proposedAuthorityLevel: 'managed',
        experienceHighlights: []
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadAvailableProjects();
    }, []);

    const loadAvailableProjects = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await projectManagerApi.getAvailableProjects();
            if (response.success) {
                setProjects(response.data || []);
            }
        } catch (err) {
            console.error('Error loading available projects:', err);
            setError('Failed to load available projects');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyClick = (project) => {
        setSelectedProject(project);
        setApplicationData({
            proposalMessage: `I am an experienced Project Manager with expertise in ${project.project_type} projects. I will provide regular updates and ensure quality delivery.`,
            proposedFeePercentage: 10.0,
            proposedAuthorityLevel: 'managed',
            experienceHighlights: []
        });
        setApplyDialogOpen(true);
    };

    const handleSubmitApplication = async () => {
        if (!selectedProject) return;

        try {
            setSubmitting(true);
            const response = await projectManagerApi.applyToProject(selectedProject.id, applicationData);
            if (response.success) {
                setApplyDialogOpen(false);
                setSelectedProject(null);
                if (onApplicationSubmitted) {
                    onApplicationSubmitted();
                }
                loadAvailableProjects(); // Refresh list
            }
        } catch (err) {
            console.error('Error submitting application:', err);
            setError(err.message || 'Failed to submit application');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Available Projects
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Projects looking for Project Managers
            </Typography>

            {projects.length === 0 ? (
                <Alert severity="info">
                    No projects available at the moment. Check back later!
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {projects.map((project) => (
                        <Grid item xs={12} md={6} key={project.id}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6" gutterBottom>
                                                {project.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {project.description}
                                            </Typography>
                                        </Box>
                                        <Chip label={project.status} size="small" color="primary" />
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AttachMoney fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    ${parseFloat(project.budget).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LocationOn fontSize="small" color="action" />
                                                <Typography variant="body2" noWrap>
                                                    {project.location}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Person fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {project.owner?.firstName} {project.owner?.lastName}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Business fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {project.project_type}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<Send />}
                                        onClick={() => handleApplyClick(project)}
                                        sx={{ mt: 2 }}
                                    >
                                        Apply to Manage
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Application Dialog */}
            <Dialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogCloseButton onClick={() => setApplyDialogOpen(false)} />
                <DialogTitle>Apply to Manage Project</DialogTitle>
                <DialogContent>
                    {selectedProject && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6">{selectedProject.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Budget: ${parseFloat(selectedProject.budget).toLocaleString()}
                            </Typography>
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Proposal Message"
                        value={applicationData.proposalMessage}
                        onChange={(e) => setApplicationData({ ...applicationData, proposalMessage: e.target.value })}
                        sx={{ mb: 2 }}
                        required
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Proposed Fee (%)"
                                value={applicationData.proposedFeePercentage}
                                onChange={(e) => setApplicationData({ ...applicationData, proposedFeePercentage: parseFloat(e.target.value) })}
                                inputProps={{ min: 3, max: 15, step: 0.5 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                select
                                label="Authority Level"
                                value={applicationData.proposedAuthorityLevel}
                                onChange={(e) => setApplicationData({ ...applicationData, proposedAuthorityLevel: e.target.value })}
                                SelectProps={{ native: true }}
                            >
                                <option value="advisory">Advisory (3-5%)</option>
                                <option value="managed">Managed (8-12%)</option>
                                <option value="delegated">Delegated (12-15%)</option>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApplyDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitApplication}
                        disabled={submitting || !applicationData.proposalMessage}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Submit Application'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PMAvailableProjects;



